import { AlertCircle, X } from "lucide-react";
import { useState, useEffect } from "react";
import { Button } from "./ui/button";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

export const CrisisModeBanner = () => {
  const [isVisible, setIsVisible] = useState(false);
  const [isClosing, setIsClosing] = useState(false);
  const { toast } = useToast();

  useEffect(() => {
    checkCrisisMode();
    
    // Poll every 30 seconds to check crisis mode status
    const interval = setInterval(checkCrisisMode, 30000);
    
    return () => clearInterval(interval);
  }, []);

  const checkCrisisMode = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { data: profile, error } = await supabase
        .from('profiles')
        .select('crisis_mode')
        .eq('user_id', user.id)
        .single();

      if (error) {
        console.error('Error fetching crisis mode:', error);
        return;
      }

      if (profile && (profile as any).crisis_mode && !isVisible) {
        setIsVisible(true);
        setIsClosing(false);
      } else if (profile && !(profile as any).crisis_mode && isVisible) {
        handleClose();
      }
    } catch (error) {
      console.error('Error checking crisis mode:', error);
    }
  };

  const handleClose = () => {
    setIsClosing(true);
    setTimeout(() => {
      setIsVisible(false);
      setIsClosing(false);
    }, 300);
  };

  const handleExitCrisisMode = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) return;

      const { error } = await supabase
        .from('profiles')
        .update({ 
          crisis_mode: false, 
          crisis_mode_activated_at: null 
        } as any)
        .eq('user_id', user.id);

      if (error) throw error;

      await supabase.from('audit_logs').insert({
        user_id: user.id,
        action: 'crisis_mode_manual_exit',
        resource_type: 'crisis_banner',
        details: { manual_dismissal: true }
      });

      toast({
        title: "Crisis support paused",
        description: "You can always return to /crisis if you need support again.",
      });

      handleClose();
    } catch (error) {
      console.error('Error exiting crisis mode:', error);
      toast({
        title: "Error",
        description: "Could not exit crisis mode. Please try again.",
        variant: "destructive"
      });
    }
  };

  if (!isVisible) return null;

  return (
    <div 
      className={`
        fixed top-16 left-0 right-0 z-50 
        bg-[hsl(42,90%,70%)] border-b-4 border-[hsl(42,90%,60%)]
        shadow-lg transition-all duration-300 ease-in-out
        ${isClosing ? 'opacity-0 -translate-y-full' : 'opacity-100 translate-y-0'}
      `}
      role="alert"
      aria-live="assertive"
    >
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between gap-4">
          <div className="flex items-center gap-3 flex-1">
            <AlertCircle className="h-6 w-6 text-[hsl(42,90%,20%)] flex-shrink-0" />
            <div className="text-[hsl(42,90%,20%)]">
              <p className="font-semibold text-sm md:text-base">
                Crisis Support Active - safety first
              </p>
              <p className="text-xs md:text-sm opacity-90">
                Your Safety Plan and emergency resources are ready at{" "}
                <a 
                  href="/crisis" 
                  className="underline hover:opacity-80 transition-opacity font-medium"
                >
                  /crisis
                </a>
              </p>
            </div>
          </div>
          
          <div className="flex items-center gap-2">
            <Button
              variant="ghost"
              size="sm"
              onClick={handleExitCrisisMode}
              className="text-[hsl(42,90%,20%)] hover:bg-[hsl(42,90%,80%)] transition-colors"
            >
              I'm safe now
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={handleClose}
              className="text-[hsl(42,90%,20%)] hover:bg-[hsl(42,90%,80%)] transition-colors h-8 w-8"
              aria-label="Dismiss banner"
            >
              <X className="h-4 w-4" />
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
};
